import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from '../context/Web3Context';
import { getPublicOrigin } from '../lib/publicUrl';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import {
    Users, Crown, Star, Zap, Copy, CheckCircle, ExternalLink,
    ChevronDown, ChevronRight, TrendingUp, Gift, Share2, Shield,
    Coins, BarChart3, ArrowRight, Wallet, Link2, MessageCircle,
    Send, Clock, Flame, Download, Award, Trophy, Hexagon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EARLY_BIRD_LIMIT = 10000;

const PACK_ICONS = { starter: Star, pro: Zap, elite: Crown };
const PACK_COLORS = { starter: '#9B6FE8', pro: '#00FF66', elite: '#FFD700' };

function formatAddr(a) {
    if (!a) return '';
    return a.slice(0, 6) + '...' + a.slice(-4);
}

function EarlyBirdBanner({ totalRegistered }) {
    const { t } = useTranslation();
    const remaining = Math.max(0, EARLY_BIRD_LIMIT - totalRegistered);
    const progress = Math.min((totalRegistered / EARLY_BIRD_LIMIT) * 100, 100);
    const isExpired = remaining === 0;
    const [pulse, setPulse] = useState(false);

    // Pulse animation every 3s for urgency
    useEffect(() => {
        if (isExpired) return;
        const interval = setInterval(() => {
            setPulse(true);
            setTimeout(() => setPulse(false), 600);
        }, 3000);
        return () => clearInterval(interval);
    }, [isExpired]);

    // Calculate urgency level
    const urgencyPercent = (totalRegistered / EARLY_BIRD_LIMIT) * 100;
    const urgencyColor = urgencyPercent > 75 ? '#FF4444' : urgencyPercent > 50 ? '#FF6B00' : '#FFD700';

    return (
        <div className="mb-8 animate-fade-in-up" data-testid="early-bird-banner" role="region" aria-label="Early Bird registration bonus">
            <div className="relative overflow-hidden rounded-2xl border border-[#FFD700]/20 bg-gradient-to-r from-[#FFD700]/10 via-[#FF6B00]/5 to-[#FFD700]/10 p-6">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#FFD700]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#FF6B00]/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />
                <div className="relative">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl bg-[#FFD700]/20 flex items-center justify-center transition-transform ${pulse ? 'scale-125' : 'scale-100'}`}>
                                <Flame className="w-5 h-5 text-[#FFD700]" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white">
                                    {isExpired ? t('sponsor.earlyBirdEnded') : t('sponsor.earlyBirdBonus')}
                                </h3>
                                <p className="text-xs text-white/40">
                                    {isExpired
                                        ? t('sponsor.earlyBirdExpired')
                                        : t('sponsor.limitedSpots')}
                                </p>
                            </div>
                        </div>
                        {!isExpired && (
                            <div className={`text-right transition-transform ${pulse ? 'scale-110' : 'scale-100'}`}>
                                <div className="text-2xl font-bold font-mono" style={{ color: urgencyColor }} data-testid="early-bird-remaining">
                                    {remaining.toLocaleString()}
                                </div>
                                <div className="text-[10px] text-white/40 uppercase tracking-wider">{t('sponsor.spotsRemainingShort')}</div>
                            </div>
                        )}
                    </div>

                    {!isExpired && (
                        <>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-center">
                                    <div className="text-xs text-white/40 mb-1">With referral code</div>
                                    <div className="text-2xl font-bold text-[#FFD700]">2 B4P</div>
                                    <div className="text-[10px] text-white/30 mt-0.5">Free on signup</div>
                                </div>
                                <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-center">
                                    <div className="text-xs text-white/40 mb-1">Without referral code</div>
                                    <div className="text-2xl font-bold text-white">1 B4P</div>
                                    <div className="text-[10px] text-white/30 mt-0.5">Free on signup</div>
                                </div>
                            </div>

                            <div className="mb-1">
                                <div className="flex justify-between text-xs mb-1.5">
                                    <span className="text-white/50">{totalRegistered.toLocaleString()} / {EARLY_BIRD_LIMIT.toLocaleString()} registered</span>
                                    <span className="font-semibold" style={{ color: urgencyColor }}>
                                        {remaining.toLocaleString()} spots remaining out of {EARLY_BIRD_LIMIT.toLocaleString()}
                                    </span>
                                </div>
                                <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all duration-1000"
                                        style={{
                                            width: `${progress}%`,
                                            background: `linear-gradient(90deg, #FFD700, ${urgencyColor})`
                                        }}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function NftBadge({ champion, month }) {
    if (!champion) return null;
    const color = PACK_COLORS[champion.pack] || '#FFD700';

    return (
        <div className="relative group" data-testid="nft-badge">
            {/* Glow effect */}
            <div className="absolute inset-0 bg-[#FFD700]/20 blur-[60px] rounded-full opacity-50 group-hover:opacity-80 transition-opacity" />
            <div className="relative w-64 mx-auto">
                {/* Badge Card */}
                <div className="relative overflow-hidden rounded-2xl border-2 border-[#FFD700]/40 bg-gradient-to-b from-[#1a1a2e] via-[#16162a] to-[#0f0f1a] p-6 text-center shadow-2xl group-hover:border-[#FFD700]/60 transition-all duration-500 group-hover:scale-[1.02]">
                    {/* Holographic shimmer */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/5 via-transparent to-[#00FF66]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#FFD700]/50 to-transparent" />
                    <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00FF66]/50 to-transparent" />

                    {/* Crown icon */}
                    <div className="relative w-16 h-16 mx-auto mb-3">
                        <div className="absolute inset-0 bg-[#FFD700]/20 rounded-full animate-pulse" />
                        <div className="relative w-full h-full rounded-full bg-gradient-to-br from-[#FFD700] to-[#FF6B00] flex items-center justify-center">
                            <Crown className="w-8 h-8 text-[#0A0A0C]" />
                        </div>
                    </div>

                    {/* Title */}
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFD700]/60 mb-1">On-Chain NFT Badge</div>
                    <h3 className="text-lg font-bold text-[#FFD700] mb-1">Sponsor of the Month</h3>
                    <div className="text-sm text-white/70 font-medium mb-3">{month}</div>

                    {/* Divider */}
                    <div className="w-12 h-px bg-gradient-to-r from-transparent via-[#FFD700]/40 to-transparent mx-auto mb-3" />

                    {/* Champion details */}
                    <div className="font-mono text-xs text-white/50 mb-2">
                        {champion.address.slice(0, 8)}...{champion.address.slice(-6)}
                    </div>
                    <Badge className="text-[10px] mb-3" style={{ background: `${color}20`, color }}>
                        {champion.pack?.toUpperCase()}
                    </Badge>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-2 mt-3">
                        <div className="p-2 rounded-lg bg-white/5">
                            <div className="text-[10px] text-white/30">Referrals</div>
                            <div className="text-sm font-bold text-[#9B6FE8]">{champion.monthly_referrals}</div>
                        </div>
                        <div className="p-2 rounded-lg bg-white/5">
                            <div className="text-[10px] text-white/30">Earned</div>
                            <div className="text-sm font-bold text-[#00FF66]">{champion.monthly_earned_eth.toFixed(4)}</div>
                        </div>
                    </div>

                    {/* Token ID */}
                    <div className="mt-3 text-[9px] text-white/20 font-mono">
                        {champion.nft_token_id
                            ? <a href={`https://basescan.org/token/0x9e83c3a3827f295e8127a71a3Aa20405695Eda2b?a=${champion.nft_token_id}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#00FF66] transition-colors">Token #{champion.nft_token_id} · Base</a>
                            : 'Base Mainnet · ERC-721'
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}

function HallOfFame({ winners }) {
    if (!winners || winners.length === 0) return null;

    return (
        <div className="space-y-2" data-testid="hall-of-fame">
            {winners.map((w, i) => (
                <div key={w.month} className="flex items-center justify-between py-2.5 px-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-[#FFD700]/20 transition-colors">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#FFD700]/10 flex items-center justify-center">
                            <Trophy className="w-4 h-4 text-[#FFD700]" />
                        </div>
                        <div>
                            <div className="text-sm font-medium text-white">{w.month}</div>
                            <div className="font-mono text-[11px] text-white/40">
                                {w.address.slice(0, 8)}...{w.address.slice(-4)}
                            </div>
                        </div>
                        <Badge className="text-[10px]" style={{ background: `${PACK_COLORS[w.pack] || '#9B6FE8'}20`, color: PACK_COLORS[w.pack] || '#9B6FE8' }}>
                            {w.pack}
                        </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-right">
                        <div>
                            <div className="text-xs text-white/30">Referrals</div>
                            <div className="text-sm font-semibold text-[#9B6FE8]">{w.monthly_referrals}</div>
                        </div>
                        <div className="w-5 h-5 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                            {w.nft_minted
                                ? <CheckCircle className="w-3 h-3 text-[#00FF66]" />
                                : <Clock className="w-3 h-3 text-[#FFD700]/50" />
                            }
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}


function PackCard({ pack, onBuy, currentPack, loading }) {
    const Icon = PACK_ICONS[pack.id] || Star;
    const color = pack.color;
    const isOwned = currentPack === pack.id;
    const packOrder = ['starter', 'pro', 'elite'];
    const canBuy = !currentPack || packOrder.indexOf(pack.id) > packOrder.indexOf(currentPack);
    const isElite = pack.id === 'elite';

    return (
        <Card className={`bg-white/[0.02] border-white/5 hover:border-white/20 transition-all card-hover relative ${isOwned ? 'border-[#00FF66]/50 ring-1 ring-[#00FF66]/20' : ''} ${isElite ? 'ring-1 ring-[#FFD700]/30' : ''}`}
              data-testid={`pack-${pack.id}`}>
            {isElite && !isOwned && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <Badge className="bg-[#FFD700] text-[#0A0A0C] text-xs font-bold px-3">POPULAR</Badge>
                </div>
            )}
            {isOwned && (
                <div className="absolute top-3 right-3">
                    <Badge className="bg-[#00FF66]/20 text-[#00FF66] text-xs">Your Pack</Badge>
                </div>
            )}
            <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${color}20` }}>
                        <Icon className="w-6 h-6" style={{ color }} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-white">{pack.name}</h3>
                        <div className="text-sm text-white/50">{pack.badge}</div>
                    </div>
                </div>

                <div className="mb-4">
                    <div className="text-3xl font-bold text-white">${pack.price_usd}</div>
                    <div className="text-sm text-white/40 font-mono">{pack.price_eth} ETH</div>
                    <div className="text-sm font-semibold mt-1" style={{ color }}>+{pack.b4p_bonus} B4P tokens</div>
                </div>

                <div className="space-y-2 mb-4">
                    <div className="text-xs text-white/40 uppercase tracking-wider">Commissions</div>
                    {Object.entries(pack.commissions).map(([level, rate]) => (
                        <div key={level} className="flex justify-between text-sm">
                            <span className="text-white/60">Level {level}</span>
                            <span className="font-semibold" style={{ color }}>{rate}%</span>
                        </div>
                    ))}
                </div>

                <div className="space-y-1.5 mb-5">
                    {pack.features.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-white/50">
                            <CheckCircle className="w-3.5 h-3.5" style={{ color }} />
                            {f}
                        </div>
                    ))}
                </div>

                <Button
                    className="w-full btn-press"
                    style={{ background: isOwned ? 'rgba(255,255,255,0.1)' : color, color: isOwned ? 'white' : '#0A0A0C' }}
                    disabled={!canBuy || loading}
                    onClick={() => onBuy(pack)}
                    data-testid={`buy-pack-${pack.id}`}
                    aria-label={isOwned ? `Current pack: ${pack.name}` : canBuy ? `Buy ${pack.name} pack for $${pack.price_usd}` : `${pack.name} - lower tier`}
                >
                    {isOwned ? 'Current Pack' : canBuy ? `Buy ${pack.name}` : 'Lower Tier'}
                </Button>
            </CardContent>
        </Card>
    );
}

function TreeNode({ node, depth = 0, maxDepth = 4 }) {
    const [expanded, setExpanded] = useState(depth < 2);
    const hasChildren = node.children && node.children.length > 0;
    const color = PACK_COLORS[node.pack] || '#9B6FE8';

    return (
        <div className={`${depth > 0 ? 'ml-6 border-l border-white/10 pl-4' : ''}`}>
            <div
                className="flex items-center gap-2 py-2 cursor-pointer hover:bg-white/5 rounded px-2 -ml-2 transition-colors"
                onClick={() => hasChildren && setExpanded(!expanded)}
            >
                {hasChildren ? (
                    expanded ? <ChevronDown className="w-4 h-4 text-white/30" /> : <ChevronRight className="w-4 h-4 text-white/30" />
                ) : <div className="w-4" />}
                <div className="w-2 h-2 rounded-full" style={{ background: color }} />
                <span className="font-mono text-sm text-white/70">{formatAddr(node.address)}</span>
                <Badge className="text-[10px]" style={{ background: `${color}20`, color }}>{node.pack || 'N/A'}</Badge>
            </div>
            {expanded && hasChildren && (
                <div className="animate-fade-in-up">
                    {node.children.map((child, i) => (
                        <TreeNode key={child.address || i} node={child} depth={depth + 1} maxDepth={maxDepth} />
                    ))}
                </div>
            )}
        </div>
    );
}

function CommissionRow({ c }) {
    const color = c.level === 0 ? '#FFD700' : '#00FF66';
    return (
        <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
            <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold" style={{ background: `${color}15`, color }}>
                    L{c.level}
                </div>
                <div>
                    <div className="text-sm text-white">{c.description || c.transaction_type}</div>
                    <div className="text-xs text-white/40">From {formatAddr(c.source)} · {c.percentage}%</div>
                </div>
            </div>
            <div className="font-mono text-sm font-semibold text-[#00FF66]">+{c.amount_eth.toFixed(6)} ETH</div>
        </div>
    );
}

export default function SponsorPage() {
    const { t } = useTranslation();
    const { isConnected, address, chainId } = useWeb3();
    const [packs, setPacks] = useState([]);
    const [profile, setProfile] = useState(null);
    const [tree, setTree] = useState(null);
    const [earnings, setEarnings] = useState(null);
    const [commissions, setCommissions] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [globalStats, setGlobalStats] = useState(null);
    const [weeklyTop, setWeeklyTop] = useState(null);
    const [monthlyChampion, setMonthlyChampion] = useState(null);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [referralInput, setReferralInput] = useState('');

    // Check URL for referral code
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get('ref');
        if (ref) setReferralInput(ref);
    }, []);

    const fetchData = useCallback(async () => {
        try {
            const [packsRes, statsRes, lbRes, weeklyRes, monthlyRes] = await Promise.all([
                fetch(`${API_URL}/api/sponsor/packs`),
                fetch(`${API_URL}/api/sponsor/stats`),
                fetch(`${API_URL}/api/sponsor/leaderboard`),
                fetch(`${API_URL}/api/sponsor/weekly-top`),
                fetch(`${API_URL}/api/sponsor/monthly-champion`)
            ]);
            if (packsRes.ok) setPacks((await packsRes.json()).packs || []);
            if (statsRes.ok) setGlobalStats(await statsRes.json());
            if (lbRes.ok) setLeaderboard((await lbRes.json()).leaderboard || []);
            if (weeklyRes.ok) setWeeklyTop(await weeklyRes.json());
            if (monthlyRes.ok) setMonthlyChampion(await monthlyRes.json());

            if (isConnected && address) {
                const [profRes, treeRes, earnRes, commRes] = await Promise.all([
                    fetch(`${API_URL}/api/sponsor/profile/${address}`),
                    fetch(`${API_URL}/api/sponsor/tree/${address}`),
                    fetch(`${API_URL}/api/sponsor/earnings/${address}`),
                    fetch(`${API_URL}/api/sponsor/commissions/${address}`)
                ]);
                if (profRes.ok) setProfile(await profRes.json());
                if (treeRes.ok) setTree(await treeRes.json());
                if (earnRes.ok) setEarnings(await earnRes.json());
                if (commRes.ok) setCommissions((await commRes.json()).commissions || []);
            }
        } catch (e) {
            console.error('Fetch error:', e);
        }
    }, [isConnected, address]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleBuyPack = async (pack) => {
        if (!isConnected) return toast.error('Connect your wallet first');
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/sponsor/buy-pack`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    address,
                    pack_id: pack.id,
                    referral_code: referralInput || null
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(`Pack ${pack.name} activated! Welcome, Sponsor!`);
                fetchData();
            } else {
                toast.error(data.error || 'Purchase failed');
            }
        } catch (e) {
            toast.error('Error: ' + e.message);
        }
        setLoading(false);
    };

    const getReferralLink = () => {
        if (!profile?.referral_code) return '';
        return `${getPublicOrigin()}/sponsor?ref=${profile.referral_code}`;
    };

    const copyReferralLink = () => {
        const link = getReferralLink();
        if (!link) return;
        navigator.clipboard.writeText(link);
        setCopied(true);
        toast.success('Referral link copied!');
        setTimeout(() => setCopied(false), 2000);
    };

    const shareOnTwitter = () => {
        const link = getReferralLink();
        if (!link) return;
        const text = `Join me on Bet4Peer! P2P betting on-chain. Use my referral link:`;
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`, '_blank');
    };

    const shareOnWhatsApp = () => {
        const link = getReferralLink();
        if (!link) return;
        const text = `Join Bet4Peer! Decentralized P2P betting. B4P bonus with my code: ${link}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    };

    const shareOnTelegram = () => {
        const link = getReferralLink();
        if (!link) return;
        const text = `Join Bet4Peer! P2P betting on-chain with B4P bonus`;
        window.open(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`, '_blank');
    };

    const downloadQR = () => {
        if (!profile?.qr_code) return;
        const a = document.createElement('a');
        a.href = `data:image/png;base64,${profile.qr_code}`;
        a.download = `bet4peer-qr-${profile.referral_code}.png`;
        a.click();
    };

    const isSponsor = profile?.is_sponsor;

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 page-enter" data-testid="sponsor-page" role="article" aria-label="Sponsor Program">
            {/* Header */}
            <div className="text-center mb-10 animate-fade-in-down">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/20 mb-4">
                    <Crown className="w-4 h-4 text-[#FFD700]" />
                    <span className="text-[#FFD700] text-sm font-medium">Multi-Level Sponsorship</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                    <span className="text-[#FFD700]">Sponsor</span> Program
                </h1>
                <p className="text-white/50 max-w-lg mx-auto">
                    Earn up to 10% commission on 4 levels of referrals. Buy a pack, share your link, earn ETH automatically.
                </p>
            </div>

            {/* Early Bird Banner */}
            {globalStats && (
                <EarlyBirdBanner totalRegistered={globalStats.total_registered || 0} />
            )}

            {/* Global Stats */}
            {globalStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <Card className="bg-white/[0.02] border-white/5 card-hover animate-fade-in-up delay-1">
                        <CardContent className="p-4 text-center">
                            <Users className="w-5 h-5 text-[#9B6FE8] mx-auto mb-2" />
                            <div className="text-2xl font-bold text-white stat-number">{globalStats.total_sponsors}</div>
                            <div className="text-xs text-white/40">Total Sponsors</div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white/[0.02] border-white/5 card-hover animate-fade-in-up delay-2">
                        <CardContent className="p-4 text-center">
                            <Coins className="w-5 h-5 text-[#00FF66] mx-auto mb-2" />
                            <div className="text-2xl font-bold text-[#00FF66] stat-number">{globalStats.total_commissions_eth.toFixed(4)}</div>
                            <div className="text-xs text-white/40">ETH Distributed</div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white/[0.02] border-white/5 card-hover animate-fade-in-up delay-3">
                        <CardContent className="p-4 text-center">
                            <Gift className="w-5 h-5 text-[#FFD700] mx-auto mb-2" />
                            <div className="text-2xl font-bold text-[#FFD700] stat-number">{globalStats.total_pack_purchases}</div>
                            <div className="text-xs text-white/40">Packs Sold</div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white/[0.02] border-white/5 card-hover animate-fade-in-up delay-4">
                        <CardContent className="p-4 text-center">
                            <Crown className="w-5 h-5 text-[#FFD700] mx-auto mb-2" />
                            <div className="text-2xl font-bold text-white stat-number">{globalStats.by_pack?.elite || 0}</div>
                            <div className="text-xs text-white/40">Elite Sponsors</div>
                        </CardContent>
                    </Card>
                </div>
            )}


            {/* Sponsor of the Month NFT Showcase */}
            {monthlyChampion?.champion && (
                <div className="mb-8 animate-fade-in-up" data-testid="monthly-champion-section">
                    <Card className="bg-gradient-to-br from-[#1a1a2e]/80 via-[#0A0A0C] to-[#1a1a2e]/80 border-[#FFD700]/20 overflow-hidden">
                        <CardContent className="p-8">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                                {/* Left: NFT Badge */}
                                <NftBadge champion={monthlyChampion.champion} month={monthlyChampion.month} />

                                {/* Right: Info */}
                                <div>
                                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/20 mb-4">
                                        <Award className="w-3.5 h-3.5 text-[#FFD700]" />
                                        <span className="text-[#FFD700] text-xs font-semibold">On-Chain Achievement</span>
                                    </div>
                                    <h2 className="text-2xl font-bold text-white mb-2">
                                        Sponsor of the <span className="text-[#FFD700]">Month</span>
                                    </h2>
                                    <p className="text-white/50 text-sm mb-6 max-w-md">
                                        The #1 sponsor each month earns an exclusive NFT badge minted on Base — a permanent on-chain proof of their achievement. Compete to win!
                                    </p>

                                    {/* Champion Stats */}
                                    <div className="grid grid-cols-3 gap-3 mb-6">
                                        <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-center">
                                            <div className="text-[10px] text-white/40 mb-1">Referrals</div>
                                            <div className="text-xl font-bold text-[#9B6FE8]">{monthlyChampion.champion.monthly_referrals}</div>
                                        </div>
                                        <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-center">
                                            <div className="text-[10px] text-white/40 mb-1">Earned</div>
                                            <div className="text-xl font-bold text-[#00FF66]">{monthlyChampion.champion.monthly_earned_eth.toFixed(4)}</div>
                                        </div>
                                        <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-center">
                                            <div className="text-[10px] text-white/40 mb-1">Pack</div>
                                            <div className="text-xl font-bold" style={{ color: PACK_COLORS[monthlyChampion.champion.pack] || '#FFD700' }}>
                                                {monthlyChampion.champion.pack?.toUpperCase()}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Hall of Fame */}
                                    {monthlyChampion.hall_of_fame && monthlyChampion.hall_of_fame.length > 0 && (
                                        <div>
                                            <h3 className="text-sm font-semibold text-white/60 mb-3 flex items-center gap-2">
                                                <Trophy className="w-4 h-4 text-[#FFD700]" />
                                                Hall of Fame
                                            </h3>
                                            <HallOfFame winners={monthlyChampion.hall_of_fame} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Sponsor Dashboard (if sponsor) */}
            {isSponsor && (
                <div className="mb-8 space-y-6">
                    {/* Referral Card with QR */}
                    <Card className="bg-gradient-to-r from-[#FFD700]/10 to-[#00FF66]/5 border-[#FFD700]/20 animate-fade-in-up">
                        <CardContent className="p-6">
                            <div className="flex flex-col lg:flex-row gap-6">
                                {/* Left: Info + Actions */}
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                        <Badge style={{ background: `${PACK_COLORS[profile.pack]}20`, color: PACK_COLORS[profile.pack] }}>
                                            {profile.pack?.toUpperCase()} SPONSOR
                                        </Badge>
                                        <span className="font-mono text-sm text-white/50">{formatAddr(address)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="text-white/40 text-sm">Your code:</span>
                                        <span className="font-mono text-lg font-bold text-[#FFD700]">{profile.referral_code}</span>
                                    </div>

                                    {/* Share Buttons Grid */}
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                        <Button onClick={copyReferralLink} className="bg-[#FFD700] text-[#0A0A0C] hover:bg-[#FFE44D] btn-press text-xs" data-testid="copy-referral-link" aria-label="Copy referral link to clipboard">
                                            {copied ? <CheckCircle className="w-3.5 h-3.5 mr-1" /> : <Link2 className="w-3.5 h-3.5 mr-1" />}
                                            {copied ? 'Copied!' : 'Copy'}
                                        </Button>
                                        <Button onClick={shareOnTwitter} variant="outline" className="border-white/10 text-white btn-press text-xs" data-testid="share-twitter" aria-label="Share on Twitter">
                                            <Share2 className="w-3.5 h-3.5 mr-1" />
                                            Twitter
                                        </Button>
                                        <Button onClick={shareOnWhatsApp} variant="outline" className="border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366]/10 btn-press text-xs" data-testid="share-whatsapp" aria-label="Share on WhatsApp">
                                            <MessageCircle className="w-3.5 h-3.5 mr-1" />
                                            WhatsApp
                                        </Button>
                                        <Button onClick={shareOnTelegram} variant="outline" className="border-[#0088cc]/30 text-[#0088cc] hover:bg-[#0088cc]/10 btn-press text-xs" data-testid="share-telegram" aria-label="Share on Telegram">
                                            <Send className="w-3.5 h-3.5 mr-1" />
                                            Telegram
                                        </Button>
                                    </div>
                                </div>

                                {/* Right: QR Code */}
                                {profile?.qr_code && (
                                    <div className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white/5 border border-white/10" data-testid="qr-code-container">
                                        <img
                                            src={`data:image/png;base64,${profile.qr_code}`}
                                            alt="QR Referral"
                                            className="w-32 h-32 rounded-lg"
                                            data-testid="qr-code"
                                        />
                                        <span className="text-[10px] text-white/30">Scan to join</span>
                                        <Button onClick={downloadQR} variant="ghost" className="text-white/50 hover:text-white text-xs h-7 px-2" data-testid="download-qr" aria-label="Download QR code image">
                                            <Download className="w-3 h-3 mr-1" />
                                            Download
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Earnings Overview */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Earnings by Level */}
                        <Card className="bg-white/[0.02] border-white/5 animate-fade-in-up delay-1">
                            <CardHeader>
                                <CardTitle className="text-lg text-white flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-[#00FF66]" />
                                    Earnings by Level
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-3xl font-bold text-[#00FF66] mb-4 stat-number">
                                    {(earnings?.total || 0).toFixed(6)} ETH
                                </div>
                                <div className="space-y-3">
                                    {[1, 2, 3, 4].map(level => {
                                        const data = earnings?.by_level?.[String(level)];
                                        return (
                                            <div key={level} className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 rounded bg-white/5 flex items-center justify-center text-xs font-bold text-white/60">
                                                        {level}
                                                    </div>
                                                    <span className="text-sm text-white/60">Level {level}</span>
                                                    <span className="text-xs text-white/30">({profile?.tree_stats?.[`level_${level}`] || 0} refs)</span>
                                                </div>
                                                <span className="font-mono text-sm text-[#00FF66]">
                                                    {(data?.earned || 0).toFixed(6)} ETH
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Referral Tree */}
                        <Card className="bg-white/[0.02] border-white/5 animate-fade-in-up delay-2">
                            <CardHeader>
                                <CardTitle className="text-lg text-white flex items-center gap-2">
                                    <Users className="w-5 h-5 text-[#9B6FE8]" />
                                    {t('sponsor.referralTree')}
                                    <Badge className="bg-white/10 text-white/60 text-xs ml-auto">
                                        {t('sponsor.totalReferrals', { count: profile?.total_referrals || 0 })}
                                    </Badge>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="max-h-[300px] overflow-y-auto">
                                {tree && tree.children && tree.children.length > 0 ? (
                                    <TreeNode node={tree} />
                                ) : (
                                    <div className="text-center py-8 text-white/30">
                                        <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                        <p>{t('sponsor.noReferrals')}</p>
                                        <p className="text-xs mt-1">{t('sponsor.shareToStart')}</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Commission History */}
                    {commissions.length > 0 && (
                        <Card className="bg-white/[0.02] border-white/5 animate-fade-in-up delay-3">
                            <CardHeader>
                                <CardTitle className="text-lg text-white flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-[#FFD700]" />
                                    {t('sponsor.commissionHistory')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {commissions.slice(0, 10).map((c, i) => (
                                    <CommissionRow key={i} c={c} />
                                ))}
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* Referral Code Input (for new users) */}
            {!isSponsor && isConnected && (
                <div className="mb-8 animate-fade-in-up">
                    <Alert className="border-[#9B6FE8]/20 bg-[#9B6FE8]/5">
                        <Gift className="w-4 h-4 text-[#9B6FE8]" />
                        <AlertDescription className="text-white/70">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                <span>{t('sponsor.gotReferral')}</span>
                                <input
                                    type="text"
                                    value={referralInput}
                                    onChange={e => setReferralInput(e.target.value.toUpperCase())}
                                    placeholder="B4P-XXXXXX"
                                    className="bg-white/10 border border-white/10 rounded px-3 py-1.5 text-sm font-mono text-white w-40"
                                    data-testid="referral-code-input"
                                    aria-label="Enter referral code"
                                />
                            </div>
                        </AlertDescription>
                    </Alert>
                </div>
            )}

            {/* Packs Grid */}
            <div className="mb-8">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2 animate-fade-in-up">
                    <Shield className="w-5 h-5 text-[#FFD700]" />
                    {isSponsor ? t('sponsor.upgradePack') : t('sponsor.choosePack')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {packs.map((pack) => (
                        <PackCard
                            key={pack.id}
                            pack={pack}
                            onBuy={handleBuyPack}
                            currentPack={profile?.pack}
                            loading={loading}
                        />
                    ))}
                </div>
            </div>

            {/* Weekly Top Sponsors */}
            {weeklyTop && weeklyTop.top_sponsors && weeklyTop.top_sponsors.length > 0 && (
                <Card className="bg-gradient-to-r from-[#FFD700]/5 to-[#FF6B00]/5 border-[#FFD700]/20 mb-6 animate-fade-in-up" data-testid="weekly-leaderboard">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <Flame className="w-5 h-5 text-[#FF6B00]" />
                            Top Sponsors This Week
                            <Badge className="bg-[#FF6B00]/20 text-[#FF6B00] text-[10px] ml-auto">LIVE</Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {weeklyTop.top_sponsors.map((s, i) => (
                                <div key={s.address} className={`flex items-center justify-between py-3 px-4 rounded-xl transition-colors ${i === 0 ? 'bg-[#FFD700]/10 border border-[#FFD700]/20' : 'hover:bg-white/5'}`} data-testid={`weekly-sponsor-${i}`}>
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-bold w-6 text-center" style={{ color: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : 'rgba(255,255,255,0.4)' }}>
                                            #{i + 1}
                                        </span>
                                        <span className="font-mono text-sm text-white/70">{formatAddr(s.address)}</span>
                                        <Badge className="text-[10px]" style={{ background: `${PACK_COLORS[s.pack] || '#9B6FE8'}20`, color: PACK_COLORS[s.pack] || '#9B6FE8' }}>
                                            {s.pack}
                                        </Badge>
                                        {s.is_top_sponsor && (
                                            <Badge className="bg-[#FFD700]/20 text-[#FFD700] text-[10px] font-bold animate-pulse" data-testid="top-sponsor-badge">
                                                TOP SPONSOR
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <div className="text-xs text-white/40">Referrals</div>
                                            <div className="font-mono text-sm font-semibold text-[#9B6FE8]">{s.weekly_referrals}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs text-white/40">Earned</div>
                                            <div className="font-mono text-sm font-semibold text-[#00FF66]">{s.weekly_earned_eth.toFixed(6)} ETH</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* All-Time Sponsor Leaderboard */}
            {leaderboard.length > 0 && (
                <Card className="bg-white/[0.02] border-white/5 animate-fade-in-up">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <Crown className="w-5 h-5 text-[#FFD700]" />
                            All-Time Top Sponsors
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {leaderboard.map((s, i) => (
                                <div key={s.address} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-white/5 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-bold w-6 text-center" style={{ color: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : 'rgba(255,255,255,0.4)' }}>
                                            #{i + 1}
                                        </span>
                                        <span className="font-mono text-sm text-white/70">{formatAddr(s.address)}</span>
                                        <Badge className="text-[10px]" style={{ background: `${PACK_COLORS[s.pack] || '#9B6FE8'}20`, color: PACK_COLORS[s.pack] || '#9B6FE8' }}>
                                            {s.pack}
                                        </Badge>
                                        {weeklyTop?.top_sponsor_address === s.address && (
                                            <Badge className="bg-[#FFD700]/20 text-[#FFD700] text-[10px] font-bold" data-testid="all-time-top-badge">
                                                TOP SPONSOR
                                            </Badge>
                                        )}
                                    </div>
                                    <span className="font-mono text-sm text-[#00FF66] font-semibold">{s.total_earned_eth.toFixed(6)} ETH</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {!isConnected && (
                <div className="mt-8 animate-fade-in-up">
                    <Alert className="border-white/10 bg-white/5">
                        <Wallet className="w-4 h-4 text-white/60" />
                        <AlertDescription className="text-white/60">
                            Connect your wallet to become a sponsor and start earning commissions
                        </AlertDescription>
                    </Alert>
                </div>
            )}
        </div>
    );
}
